import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { authService, firebaseAuthService } from "./services";
import { setUnauthorizedHandler, tokenStore } from "./api";
import { clearUserCaches } from "./pwa";
import { isFirebaseConfigured } from "./firebase-config";
import type { LoginPayload, RegisterPayload, User } from "@/types/api";

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (payload: LoginPayload) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/*
  The Firebase SDK is fetched only when it is about to be used. It is around
  120 kB that the discovery experience never touches, and this provider mounts
  on every page — so a static import would put it on the critical path of the
  very first paint.
*/
async function bridgeFirebase(): Promise<void> {
  if (!isFirebaseConfigured()) return;
  try {
    const { token } = await firebaseAuthService.customToken();
    if (!token) return;
    const { signInWithCustomTokenSafe } = await import("./firebase");
    await signInWithCustomTokenSafe(token);
  } catch (err) {
    // Non-fatal — chat just won't work until backend Firebase service is configured.
    console.warn("[firebase] custom token bridge failed", err);
  }
}

/** Sign out of Firebase without dragging the SDK into this chunk. */
async function releaseFirebaseSession(): Promise<void> {
  if (!isFirebaseConfigured()) return;
  try {
    const { firebaseSignOut } = await import("./firebase");
    await firebaseSignOut();
  } catch {
    /* noop */
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => tokenStore.get());
  const [isLoading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const clearLocal = useCallback(() => {
    tokenStore.clear();
    setToken(null);
    setUser(null);
    queryClient.clear();
    void releaseFirebaseSession();
    /*
      In-memory cache is not the only copy any more. The service worker keeps
      catalogue responses and listing photographs in Cache Storage so the app
      works with no connection, and those survive a reload. On a shared phone —
      which in this market is the common case, not the edge case — the next
      person signing in should not find the last one's browsing sitting there.
    */
    void clearUserCaches();
  }, [queryClient]);

  useEffect(() => {
    setUnauthorizedHandler(() => clearLocal());
    return () => setUnauthorizedHandler(null);
  }, [clearLocal]);

  // Bootstrap session
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      const stored = tokenStore.get();
      if (!stored) {
        setLoading(false);
        return;
      }
      try {
        const me = await authService.me();
        if (cancelled) return;
        setUser(me);
        setToken(stored);
        void bridgeFirebase();
      } catch {
        if (!cancelled) clearLocal();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [clearLocal]);

  const persist = useCallback((newToken: string, newUser: User) => {
    tokenStore.set(newToken);
    setToken(newToken);
    setUser(newUser);
    void bridgeFirebase();
  }, []);

  const login = useCallback(
    async (payload: LoginPayload) => {
      const res = await authService.login(payload);
      persist(res.access_token, res.user);
    },
    [persist],
  );

  const register = useCallback(
    async (payload: RegisterPayload) => {
      const res = await authService.register(payload);
      persist(res.access_token, res.user);
    },
    [persist],
  );

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      /* ignore */
    }
    try {
      await firebaseAuthService.revoke();
    } catch {
      /* ignore */
    }
    clearLocal();
  }, [clearLocal]);

  const refresh = useCallback(async () => {
    try {
      const me = await authService.me();
      setUser(me);
    } catch {
      clearLocal();
    }
  }, [clearLocal]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(user && token),
      isLoading,
      login,
      register,
      logout,
      refresh,
    }),
    [user, token, isLoading, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
